/**
 * Contact Information settings — admin page.
 *
 * Route: /crm/settings/contact
 * Persists under CMS section key "contact" (see backend CMS_DEFAULTS).
 * The public /contact page reads the same document via useCmsSection('contact').
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Phone, Mail, MapPin, Clock, Save, RotateCcw, Plus, Trash2, ArrowUp, ArrowDown,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const CrmContactSettings = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/admin/cms/content/contact`, { withCredentials: true });
      setData(r.data || {});
    } catch {
      toast.error('Failed to load contact settings');
    }
  };
  useEffect(() => { load(); }, []);

  const setField = (k, v) => setData((d) => ({ ...d, [k]: v }));
  const setAddr = (k, v) => setData((d) => ({ ...d, address: { ...(d?.address || {}), [k]: v } }));
  const setHour = (idx, patch) => setData((d) => ({
    ...d,
    hours: (d.hours || []).map((h, i) => (i === idx ? { ...h, ...patch } : h)),
  }));
  const addHour = () => setData((d) => ({
    ...d,
    hours: [...(d?.hours || []), { label: 'New row', open: '10 AM', close: '6 PM', closed: false }],
  }));
  const delHour = (idx) => setData((d) => ({
    ...d,
    hours: (d.hours || []).filter((_, i) => i !== idx),
  }));
  const moveHour = (idx, delta) => setData((d) => {
    const list = [...(d.hours || [])];
    const j = idx + delta;
    if (j < 0 || j >= list.length) return d;
    [list[idx], list[j]] = [list[j], list[idx]];
    return { ...d, hours: list };
  });

  const save = async () => {
    if (!data) return;
    if (!data.email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email)) {
      toast.error('Please enter a valid email'); return;
    }
    if (!data.phone) { toast.error('Phone number is required'); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/admin/cms/content/contact`,
        { data },
        { withCredentials: true },
      );
      toast.success('Saved — public /contact page updated immediately');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Reset Contact Information to bundled defaults? Your custom phone/email/hours will be lost.')) return;
    setResetting(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/admin/cms/content/contact/reset`,
        {},
        { withCredentials: true },
      );
      setData(r.data?.data || {});
      toast.success('Reset to defaults');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setResetting(false);
    }
  };

  if (!data) return <div className="text-center py-10 text-[#6B6B7A]">Loading…</div>;

  const addr = data.address || {};

  return (
    <div className="space-y-5" data-testid="crm-contact-settings-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1E1E24]" style={{ fontFamily: 'Fredoka' }}>Contact Information</h1>
          <p className="text-sm text-[#6B6B7A] mt-1">
            Phone, email, store address and operating hours shown on the public <code className="text-xs">/contact</code> page and in the site footer.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={save}
            disabled={saving}
            className="bg-[#FF3B6B] hover:bg-[#FF3B6B]/90 text-white rounded-full"
            data-testid="contact-save-btn"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
          <Button
            onClick={resetDefaults}
            disabled={resetting}
            variant="outline"
            className="rounded-full"
            data-testid="contact-reset-btn"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" />
            {resetting ? 'Resetting…' : 'Reset to defaults'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Primary contact */}
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-[#FF3B6B]" />
            <h3 className="font-semibold text-sm text-[#1E1E24]">Primary contact</h3>
          </div>
          <div>
            <Label className="text-xs">Phone (display)</Label>
            <Input
              value={data.phone || ''}
              onChange={(e) => setField('phone', e.target.value)}
              placeholder="905-616-7617"
              data-testid="contact-input-phone-display"
            />
          </div>
          <div>
            <Label className="text-xs">Phone — tel: link target</Label>
            <Input
              value={data.phone_tel || ''}
              onChange={(e) => setField('phone_tel', e.target.value)}
              placeholder="+19056167617"
              data-testid="contact-input-phone-tel"
            />
            <p className="text-[11px] text-[#6B6B7A] mt-1">What the "Call us" link dials. E.164 format recommended (+1…).</p>
          </div>
          <div>
            <Label className="text-xs">Email</Label>
            <Input
              type="email"
              value={data.email || ''}
              onChange={(e) => setField('email', e.target.value)}
              placeholder="info@creativebirthdays.ca"
              data-testid="contact-input-email"
            />
          </div>
        </div>

        {/* Store address */}
        <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#FF3B6B]" />
            <h3 className="font-semibold text-sm text-[#1E1E24]">Store address</h3>
          </div>
          <div>
            <Label className="text-xs">Street</Label>
            <Input
              value={addr.street || ''}
              onChange={(e) => setAddr('street', e.target.value)}
              placeholder="1500 Upper Middle Road West, Unit 4"
              data-testid="contact-input-street"
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">City</Label>
              <Input
                value={addr.city || ''}
                onChange={(e) => setAddr('city', e.target.value)}
                placeholder="Oakville"
                data-testid="contact-input-city"
              />
            </div>
            <div>
              <Label className="text-xs">Province</Label>
              <Input
                value={addr.province || ''}
                onChange={(e) => setAddr('province', e.target.value)}
                placeholder="ON"
                maxLength={3}
                data-testid="contact-input-province"
              />
            </div>
            <div>
              <Label className="text-xs">Postal code</Label>
              <Input
                value={addr.postal_code || ''}
                onChange={(e) => setAddr('postal_code', e.target.value)}
                placeholder="L6M 3G3"
                maxLength={8}
                data-testid="contact-input-postal-code"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Operating hours */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#FF3B6B]" />
            <h3 className="font-semibold text-sm text-[#1E1E24]">Operating hours</h3>
          </div>
          <Button variant="outline" size="sm" onClick={addHour} className="rounded-full" data-testid="contact-hours-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add row
          </Button>
        </div>
        <div>
          <Label className="text-xs">Subtitle (above the hours list)</Label>
          <Input
            value={data.hours_subtitle || ''}
            onChange={(e) => setField('hours_subtitle', e.target.value)}
            placeholder="We're open 7 days a week."
            data-testid="contact-input-hours-subtitle"
          />
        </div>
        <div className="space-y-2">
          {(data.hours || []).map((h, i) => (
            <div
              key={i}
              className="rounded-xl border border-[#E8E5DF] p-3 bg-[#FAF8F4] grid grid-cols-[auto,1fr,auto,1fr,1fr,auto] sm:grid-cols-[auto,1.5fr,auto,1fr,1fr,auto] items-center gap-2"
              data-testid={`contact-hours-row-${i}`}
            >
              <div className="flex flex-col">
                <Button variant="ghost" size="icon" className="h-5 w-5" disabled={i === 0} onClick={() => moveHour(i, -1)}>
                  <ArrowUp className="w-3 h-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-5 w-5" disabled={i === (data.hours || []).length - 1} onClick={() => moveHour(i, 1)}>
                  <ArrowDown className="w-3 h-3" />
                </Button>
              </div>
              <Input
                value={h.label || ''}
                onChange={(e) => setHour(i, { label: e.target.value })}
                placeholder="Mon – Fri"
                data-testid={`contact-hours-label-${i}`}
              />
              <label className="flex items-center gap-2 text-xs text-[#6B6B7A]">
                <Switch
                  checked={!h.closed}
                  onCheckedChange={(v) => setHour(i, { closed: !v })}
                  data-testid={`contact-hours-open-${i}`}
                />
                {h.closed ? 'Closed' : 'Open'}
              </label>
              <Input
                value={h.open || ''}
                onChange={(e) => setHour(i, { open: e.target.value })}
                placeholder="9 AM"
                disabled={!!h.closed}
                data-testid={`contact-hours-open-time-${i}`}
              />
              <Input
                value={h.close || ''}
                onChange={(e) => setHour(i, { close: e.target.value })}
                placeholder="7 PM"
                disabled={!!h.closed}
                data-testid={`contact-hours-close-time-${i}`}
              />
              <Button variant="ghost" size="icon" onClick={() => delHour(i)} data-testid={`contact-hours-delete-${i}`}>
                <Trash2 className="w-3.5 h-3.5 text-red-600" />
              </Button>
            </div>
          ))}
          {(data.hours || []).length === 0 && (
            <p className="text-center text-sm text-[#6B6B7A] py-4 italic">No hours set. Click "Add row" to start.</p>
          )}
        </div>
        <div>
          <Label className="text-xs">Footnote under the hours list</Label>
          <Input
            value={data.hours_note || ''}
            onChange={(e) => setField('hours_note', e.target.value)}
            placeholder="Last party booking starts 2 hours before closing."
            data-testid="contact-input-hours-note"
          />
        </div>
      </div>

      {/* Page copy */}
      <div className="bg-white rounded-2xl border border-[#E8E5DF] p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#FF3B6B]" />
          <h3 className="font-semibold text-sm text-[#1E1E24]">Contact-page copy</h3>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Eyebrow</Label>
            <Input
              value={data.page_eyebrow || ''}
              onChange={(e) => setField('page_eyebrow', e.target.value)}
              placeholder="Say hello"
              data-testid="contact-input-eyebrow"
            />
          </div>
          <div className="lg:col-span-2">
            <Label className="text-xs">Page title</Label>
            <Input
              value={data.page_title || ''}
              onChange={(e) => setField('page_title', e.target.value)}
              placeholder="Questions before the big day?"
              data-testid="contact-input-title"
            />
          </div>
        </div>
        <div>
          <Label className="text-xs">Intro paragraph</Label>
          <Textarea
            rows={3}
            value={data.page_intro || ''}
            onChange={(e) => setField('page_intro', e.target.value)}
            placeholder="Whether you're juggling allergy needs…"
            data-testid="contact-input-intro"
          />
        </div>
        <div>
          <Label className="text-xs">Reply-time note (below phone/email)</Label>
          <Textarea
            rows={2}
            value={data.page_reply_note || ''}
            onChange={(e) => setField('page_reply_note', e.target.value)}
            placeholder="We typically reply within one business day…"
            data-testid="contact-input-reply-note"
          />
        </div>
      </div>
    </div>
  );
};

export default CrmContactSettings;
